/**
 * CardScanner — guided multi-tilt camera capture with desktop file upload fallback.
 *
 * Capture flow:
 *   1. User picks side: front / back (or "front + back").
 *   2. For each side we collect THREE captures: flat, tilt-left, tilt-right.
 *      (Phase 1 only the FLAT capture is consumed by centering; the tilt
 *      captures are uploaded as `frontTilt1/2` / `backTilt1/2` and reserved
 *      for the surface stage in Phase 2 — no client change needed later.)
 *   3. Each capture is auto-cropped to the on-screen overlay rectangle so the
 *      server receives an image whose pixels ARE the card's printed area.
 *   4. Sharpness + stability gates auto-fire the capture; manual button is
 *      always available.
 *
 * Fallback:
 *   - If getUserMedia is unavailable or the user opts out, show a file input.
 *   - File-mode skips tilt prompts (user uploads what they have) — server
 *     gracefully degrades (Phase 2 surface stage will downgrade confidence).
 *
 * Output (via onCapture prop):
 *   { front: Blob, back?: Blob, frontTilt1?, frontTilt2?, backTilt1?, backTilt2? }
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';

const CARD_ASPECT = 5 / 7;     // width / height — PSA card 2.5×3.5
// Overlay-fraction = how much of the visible wrapper the framing box
// covers. 1.0 = the green frame is at the wrapper edge and the capture
// takes the ENTIRE visible region — guarantees what the user sees in the
// live feed equals what they get in the still preview (zero zoom).
const OVERLAY_FRAC = 1.0;
const OUTPUT_W = 750;          // canonical export width
const OUTPUT_H = Math.round(OUTPUT_W / CARD_ASPECT); // 1050

// Sharpness threshold (variance of Laplacian on a downsampled luma frame).
// Values < 80 = blurry on most phones; 150+ = clean. Auto-capture requires
// the scene to clear `SHARP_OK` for STABILITY_FRAMES consecutive frames AND
// at least ARM_DELAY_MS to have passed since the live view started — so
// users have time to position before any shot fires.
const SHARP_OK = 130;             // a touch stricter than before
const STABILITY_FRAMES = 24;      // ~0.8 s @ 30 fps (was ~0.13 s)
const ARM_DELAY_MS = 2000;        // grace period before auto-capture can fire

const TILT_PROMPTS = [
  { key: '',      label: 'Hold flat — line up inside the box' },
  { key: 'Tilt1', label: 'Tilt LEFT ~15° — same framing' },
  { key: 'Tilt2', label: 'Tilt RIGHT ~15° — same framing' },
];

function laplacianVariance(imageData) {
  // Approximate variance-of-Laplacian on the luma channel using a 3×3 kernel
  // [0,1,0; 1,-4,1; 0,1,0]. We sample a centre crop to keep this cheap.
  const { data, width, height } = imageData;
  const cx0 = Math.floor(width * 0.25), cx1 = Math.floor(width * 0.75);
  const cy0 = Math.floor(height * 0.25), cy1 = Math.floor(height * 0.75);
  let sum = 0, sumSq = 0, n = 0;
  for (let y = cy0 + 1; y < cy1 - 1; y += 2) {
    for (let x = cx0 + 1; x < cx1 - 1; x += 2) {
      const i = (y * width + x) * 4;
      // luma approx (0.299R + 0.587G + 0.114B) on the cell + neighbours
      const lum = (px) => 0.299 * data[px] + 0.587 * data[px + 1] + 0.114 * data[px + 2];
      const c  = lum(i);
      const u  = lum(i - width * 4);
      const d  = lum(i + width * 4);
      const l  = lum(i - 4);
      const r  = lum(i + 4);
      const v  = (u + d + l + r) - 4 * c;
      sum += v; sumSq += v * v; n++;
    }
  }
  if (!n) return 0;
  const mean = sum / n;
  return (sumSq / n) - (mean * mean);
}

// Sprint 6: returns true when the centre crop has a hot-spot consistent
// with overhead-light glare on a glossy card. Heuristic:
//   - count pixels with luminance > 245 (saturated highlight)
//   - if that's > 3.5% of sampled pixels AND the mean luminance is below
//     200 (so we're not just looking at an evenly-bright white card)
//     → flag glare
// Cheap: runs on the same downsampled probe used for sharpness.
function detectGlare(imageData) {
  const { data, width, height } = imageData;
  const cx0 = Math.floor(width * 0.2), cx1 = Math.floor(width * 0.8);
  const cy0 = Math.floor(height * 0.2), cy1 = Math.floor(height * 0.8);
  let hot = 0, total = 0, lumSum = 0;
  for (let y = cy0; y < cy1; y += 2) {
    for (let x = cx0; x < cx1; x += 2) {
      const i = (y * width + x) * 4;
      const lum = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      if (lum > 245) hot++;
      lumSum += lum;
      total++;
    }
  }
  if (total === 0) return false;
  const meanLum = lumSum / total;
  const hotPct = hot / total;
  return hotPct > 0.035 && meanLum < 200;
}

function cropToOverlay(videoEl) {
  // Capture the SAME rectangle the user lines up under the green box in
  // the live view — accounting for `object-fit: cover` which letterboxes
  // either width or height depending on the source aspect ratio.
  //
  // Steps:
  //   1. Determine which dimension `object-fit: cover` crops in the
  //      wrapper. Source wider than 5:7 → wrapper crops the sides.
  //      Source taller than 5:7 → wrapper crops top + bottom.
  //   2. Compute the visible source rect (the slice the user actually sees
  //      under the SVG overlay).
  //   3. The green box covers OVERLAY_FRAC of that visible rect, centered.
  //      Capture exactly that — no more, no less.
  //
  // Without this correction, a 1080×1920 phone-portrait source would have
  // the wrapper showing the central ~70% vertically, but the old math
  // captured the FULL 1920-tall × 1340-wide rect — producing a saved image
  // with extra content above/below the framed card.
  const vw = videoEl.videoWidth, vh = videoEl.videoHeight;
  if (!vw || !vh) return null;

  const sourceAspect  = vw / vh;
  const wrapperAspect = CARD_ASPECT;     // 5/7
  let visibleW, visibleH;
  if (sourceAspect > wrapperAspect) {
    // Source wider than 5:7 → wrapper crops the SIDES, full height visible.
    visibleH = vh;
    visibleW = vh * wrapperAspect;
  } else {
    // Source taller than 5:7 → wrapper crops TOP+BOTTOM, full width visible.
    visibleW = vw;
    visibleH = vw / wrapperAspect;
  }

  const boxW = Math.floor(visibleW * OVERLAY_FRAC);
  const boxH = Math.floor(visibleH * OVERLAY_FRAC);
  const boxX = Math.floor((vw - boxW) / 2);
  const boxY = Math.floor((vh - boxH) / 2);

  // Only downscale on output if source rect exceeds canvas cap.
  const scale = Math.min(1, OUTPUT_H / boxH);
  const outW = Math.max(120, Math.round(boxW * scale));
  const outH = Math.max(168, Math.round(boxH * scale));

  const canvas = document.createElement('canvas');
  canvas.width  = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(videoEl, boxX, boxY, boxW, boxH, 0, 0, outW, outH);
  return canvas;
}

function canvasToBlob(canvas, quality = 0.92) {
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/jpeg', quality));
}

export default function CardScanner({ mode = 'front', onSubmit, onFrontCaptured, busy = false }) {
  // mode: 'front' | 'frontBack'
  const wantBack = mode === 'frontBack';

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const stableCountRef = useRef(0);
  const captureLockRef = useRef(false);

  const [stage, setStage] = useState('idle'); // idle | live | done
  const [step, setStep] = useState(0);        // 0..(wantBack?5:2)
  const [sharpness, setSharpness] = useState(0);
  const [error, setError] = useState(null);
  const [captured, setCaptured] = useState({}); // { front: Blob, frontTilt1, ... }
  const [useFiles, setUseFiles] = useState(false);
  // Pending capture awaiting user confirmation. When set, the live view is
  // replaced with a "Use this / Retake" preview so the user always approves
  // each shot before it's banked.
  const [pending, setPending] = useState(null);  // { blob, url, field } | null
  // Auto-capture is OPT-IN. Default to manual: the user always taps the
  // Capture button. Saves them from a "fired before I was ready" experience.
  const [autoCapture, setAutoCapture] = useState(false);
  // Sprint 6: torch (flashlight) toggle. Only some mobile browsers expose
  // `torch` in MediaStreamTrack capabilities — we feature-detect after
  // the stream resolves and hide the button when unsupported.
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  // Sprint 6: glare warning. Set when the per-frame probe detects too many
  // saturated highlights inside the framing rectangle (likely overhead
  // light bouncing off a glossy card). Surfaced near the sharpness label.
  const [glare, setGlare] = useState(false);
  // Sprint 6: one-time lighting-tips card shown above the viewfinder on
  // the very first live frame. Auto-dismisses after a captured shot.
  const [tipsDismissed, setTipsDismissed] = useState(false);
  // Earliest timestamp at which auto-capture is allowed to fire after going
  // live or after a captured shot is approved.
  const armAtRef = useRef(0);
  // Per-capture pre-flight validation results.
  // Shape: { [field]: { status: 'pending'|'ok'|'fail', issues: [], metrics: {} } }
  const [validations, setValidations] = useState({});

  // Build the ordered list of (side, tiltKey, label) we need to capture.
  const plan = React.useMemo(() => {
    const sides = wantBack ? ['front', 'back'] : ['front'];
    return sides.flatMap(side =>
      TILT_PROMPTS.map(t => ({
        side,
        field: side + (t.key ? t.key : ''), // 'front', 'frontTilt1', 'back', ...
        label: `${side === 'front' ? 'FRONT' : 'BACK'}: ${t.label}`,
      }))
    );
  }, [wantBack]);

  const currentTask = plan[step];
  const allDone = step >= plan.length;

  // ---- Camera lifecycle ----
  // iOS Safari is the strictest browser here. The contract it enforces:
  //   1. getUserMedia must be called from a user-gesture handler (button click).
  //   2. The <video> element must already be in the DOM when the stream
  //      resolves — so we ALWAYS render it (display: none until live).
  //   3. video.srcObject = stream and video.play() must run synchronously
  //      after `await getUserMedia` inside that same gesture's microtask
  //      chain — no requestAnimationFrame, no setTimeout indirection.
  //   4. The element needs `playsInline` AND `webkit-playsinline` AND `muted`.
  //   5. Insecure (HTTP) origins silently get a null mediaDevices on iOS.
  const startCamera = useCallback(async () => {
    setError(null);
    if (!window.isSecureContext) {
      setError('Camera requires HTTPS. The page is being served over HTTP.');
      setUseFiles(true);
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Camera not available in this browser. Use the file upload below.');
      setUseFiles(true);
      return;
    }
    try {
      // Prefer the rear camera on phones; fall back gracefully to whatever the
      // device offers (most laptops / desktops only have a front-facing webcam).
      // Drop fixed width/height — iOS often refuses constraint sets that ask
      // for resolutions the camera can't deliver.
      // `ideal:` constraints are NEGOTIATED on iOS (not required), so we
      // can ask for high resolution without breaking phones that can't
      // deliver. This avoids the 640×480 default that produces blurry
      // captures after our 5:7 portrait crop is upscaled to the export
      // canvas.
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: { ideal: 'environment' },
            width:  { ideal: 1920 },
            height: { ideal: 1080 },
          },
        });
      } catch (_) {
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            audio: false,
            video: { width: { ideal: 1280 }, height: { ideal: 720 } },
          });
        } catch (_) {
          // Last fallback: any camera at any resolution.
          stream = await navigator.mediaDevices.getUserMedia({ audio: false, video: true });
        }
      }
      streamRef.current = stream;
      // Sprint 6: detect torch support so we know whether to render the
      // flashlight toggle. Only mobile (Chrome Android, some PWAs) exposes
      // this; iOS Safari historically does not. Hidden cleanly when missing.
      try {
        const track = stream.getVideoTracks()[0];
        const caps = track && typeof track.getCapabilities === 'function' ? track.getCapabilities() : {};
        setTorchSupported(!!(caps && 'torch' in caps));
      } catch (_) { setTorchSupported(false); }
      // Attach the stream IMMEDIATELY (the <video> element is always mounted —
      // see render). Doing this before setState keeps the user-gesture context.
      const v = videoRef.current;
      if (v) {
        v.srcObject = stream;
        const playP = v.play();
        if (playP && typeof playP.catch === 'function') {
          playP.catch(() => setError('Tap the video to start the camera (autoplay was blocked).'));
        }
      }
      // Arm auto-capture only after the grace period — gives the user time
      // to position the card before any shot fires.
      armAtRef.current = Date.now() + ARM_DELAY_MS;
      setStage('live');
    } catch (e) {
      const name = e?.name || 'error';
      const msg = name === 'NotAllowedError' || name === 'PermissionDeniedError'
        ? 'Camera permission was denied. Enable it in Settings → Safari/Chrome → Camera, or switch to file upload.'
        : name === 'NotFoundError' || name === 'DevicesNotFoundError'
        ? 'No camera found on this device. Use file upload instead.'
        : name === 'NotReadableError'
        ? 'Camera is already in use by another app. Close it and try again.'
        : `Camera unavailable (${name}). Use file upload instead.`;
      setError(msg);
      setUseFiles(true);
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setTorchOn(false);
    setTorchSupported(false);
  }, []);

  // Sprint 6: toggle the device torch. Wraps applyConstraints so a
  // browser-that-pretends-to-support-torch-but-actually-doesn't can't crash
  // the camera flow.
  const toggleTorch = useCallback(async () => {
    const stream = streamRef.current;
    const track = stream?.getVideoTracks?.()[0];
    if (!track) return;
    try {
      const next = !torchOn;
      await track.applyConstraints({ advanced: [{ torch: next }] });
      setTorchOn(next);
    } catch (e) {
      setError('This device camera refused the flashlight request. Try turning on a desk lamp instead.');
      setTorchSupported(false);
    }
  }, [torchOn]);

  useEffect(() => () => stopCamera(), [stopCamera]);

  // ---- Per-frame quality monitor ----
  useEffect(() => {
    if (stage !== 'live' || allDone || pending) return;
    const v = videoRef.current;
    if (!v) return;
    const probe = document.createElement('canvas');
    probe.width = 240; probe.height = 320; // small luma probe for laplacian
    const pctx = probe.getContext('2d', { willReadFrequently: true });

    const tick = () => {
      if (!videoRef.current || stage !== 'live') return;
      try {
        if (v.videoWidth && v.videoHeight) {
          // Same visible-rect math as cropToOverlay so the sharpness probe
          // samples exactly what the user sees under the green box.
          const vw = v.videoWidth, vh = v.videoHeight;
          const sourceAspect = vw / vh;
          let visibleW, visibleH;
          if (sourceAspect > CARD_ASPECT) {
            visibleH = vh; visibleW = vh * CARD_ASPECT;
          } else {
            visibleW = vw; visibleH = vw / CARD_ASPECT;
          }
          const ow = Math.floor(visibleW * OVERLAY_FRAC);
          const oh = Math.floor(visibleH * OVERLAY_FRAC);
          const ox = Math.floor((vw - ow) / 2);
          const oy = Math.floor((vh - oh) / 2);
          pctx.drawImage(v, ox, oy, ow, oh, 0, 0, probe.width, probe.height);
          const data = pctx.getImageData(0, 0, probe.width, probe.height);
          const lap = laplacianVariance(data);
          setSharpness(lap);
          // Sprint 6: glare = too many pixels in the saturated-highlight
          // bin (lum > 245) AND average luminance not already washed-out
          // (which would mean overall over-exposure, not glare). Threshold
          // tuned empirically against indoor overhead-light glossy cards.
          setGlare(detectGlare(data));
          if (lap >= SHARP_OK) {
            stableCountRef.current += 1;
            // Auto-capture only fires when (a) opt-in toggle is ON,
            // (b) grace period since stage='live' has elapsed,
            // (c) stable-sharp for STABILITY_FRAMES consecutive frames,
            // (d) the camera is delivering at least 720p source so we don't
            //     bank a low-res blur.
            if (autoCapture
                && Date.now() >= armAtRef.current
                && v.videoWidth >= 720
                && stableCountRef.current >= STABILITY_FRAMES
                && !captureLockRef.current) {
              captureLockRef.current = true;
              doCapture();
            }
          } else {
            stableCountRef.current = 0;
          }
        }
      } catch (_) { /* probe errors are non-fatal */ }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, allDone, step, pending, autoCapture]);

  const doCapture = useCallback(async () => {
    const v = videoRef.current;
    if (!v) { captureLockRef.current = false; return; }
    const canvas = cropToOverlay(v);
    if (!canvas) { captureLockRef.current = false; return; }
    const blob = await canvasToBlob(canvas, 0.92);
    if (!blob) { captureLockRef.current = false; return; }
    const task = plan[step];
    // Park the shot in `pending`. The user reviews it on a still preview and
    // either Approves (banks it + advances) or Retakes (drops it + resumes
    // the live feed at the same step).
    setPending({ blob, url: URL.createObjectURL(blob), field: task.field });
    stableCountRef.current = 0;
    // captureLock stays true while preview is up so the auto-capture loop
    // doesn't fire another shot behind the preview.
  }, [plan, step]);

  // User approved the pending shot — bank it, kick off async validation,
  // then advance to the next step.
  const approvePending = useCallback(() => {
    if (!pending) return;
    const { blob, url, field } = pending;
    setCaptured(prev => ({ ...prev, [field]: blob }));
    if (field === 'front' && onFrontCaptured) onFrontCaptured(blob);
    URL.revokeObjectURL(url);
    setPending(null);
    setTipsDismissed(true); // user has the rhythm now; hide tips
    setStep(s => s + 1);
    armAtRef.current = Date.now() + ARM_DELAY_MS;
    setTimeout(() => { captureLockRef.current = false; }, 400);
    // Fire-and-forget validation. The thumbnail + summary panel show its
    // result as soon as the server responds (typically <500 ms).
    setValidations(v => ({ ...v, [field]: { status: 'pending' } }));
    runValidation(field, blob);
  }, [pending, onFrontCaptured]);

  // Hit /api/grading/validate for a single captured image.
  const runValidation = useCallback(async (field, blob) => {
    try {
      const fd = new FormData();
      fd.append('image', blob, `${field}.jpg`);
      fd.append('side', field.startsWith('back') ? 'back' : 'front');
      const res = await fetch('/api/grading/validate', { method: 'POST', body: fd });
      const data = await res.json();
      setValidations(v => ({
        ...v,
        [field]: {
          status: data.ok ? 'ok' : 'fail',
          issues: data.issues || [],
          metrics: data.metrics || null,
        },
      }));
    } catch (e) {
      setValidations(v => ({
        ...v,
        [field]: {
          status: 'fail',
          issues: [{ severity: 'error', code: 'NETWORK', message: 'Could not reach validation service.' }],
          metrics: null,
        },
      }));
    }
  }, []);

  // User rejected the pending shot — discard it and resume the live feed.
  const rejectPending = useCallback(() => {
    if (!pending) return;
    URL.revokeObjectURL(pending.url);
    setPending(null);
    captureLockRef.current = false;
  }, [pending]);

  const retake = (idx) => {
    const f = plan[idx].field;
    setCaptured(prev => {
      const next = { ...prev };
      delete next[f];
      return next;
    });
    setValidations(prev => {
      const next = { ...prev };
      delete next[f];
      return next;
    });
    setStep(idx);
    captureLockRef.current = false;
  };

  // ---- File-upload fallback ----
  const onFiles = (which) => (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\/(jpe?g|png|webp)$/i.test(f.type)) {
      setError('Only JPEG, PNG, or WebP files.');
      return;
    }
    if (f.size > 12 * 1024 * 1024) { setError('Max 12 MB per file.'); return; }
    setError(null);
    setCaptured(prev => ({ ...prev, [which]: f }));
    if (which === 'front' && onFrontCaptured) onFrontCaptured(f);
    setValidations(v => ({ ...v, [which]: { status: 'pending' } }));
    runValidation(which, f);
  };

  const submit = async () => {
    if (!captured.front) { setError('Capture or upload the FRONT first.'); return; }
    setError(null);
    onSubmit && onSubmit(captured);
  };

  const startOver = () => {
    if (Object.keys(captured).length > 0 && !window.confirm('Discard all captured images and start over?')) return;
    if (pending) { URL.revokeObjectURL(pending.url); setPending(null); }
    setCaptured({});
    setStep(0);
    setError(null);
    captureLockRef.current = false;
    if (onFrontCaptured) onFrontCaptured(null);
  };

  const overallReady = !!captured.front && (!wantBack || !!captured.back);
  // Are all captured shots either ok or still pending validation? We allow
  // pending (don't block while server is processing), but block on any
  // explicit failure.
  const anyFailed = Object.entries(validations).some(([f, v]) => captured[f] && v?.status === 'fail');
  const submitDisabled = !overallReady || busy || anyFailed;

  // ---- Render ----
  // The <video> element is mounted continuously inside an always-rendered
  // wrapper whose visibility is controlled by `stage`. iOS Safari requires
  // the video element to be in the DOM at the moment getUserMedia resolves;
  // remounting it on stage transitions silently drops the stream.
  const liveActive = !useFiles && stage === 'live' && !allDone && currentTask;
  return (
    <div className="card-scanner" style={{ maxWidth: 720, margin: '0 auto' }}>
      {error && (
        <div role="alert" style={{ background: '#fdecea', color: '#611a15', padding: 12, borderRadius: 6, marginBottom: 12 }}>
          {error}
        </div>
      )}

      {!useFiles && stage === 'idle' && (
        <button type="button" onClick={startCamera} style={btnPrimary}>
          Start camera
        </button>
      )}

      {/* Sprint 6: one-time lighting tips card. Shown above the viewfinder
          on the first capture and dismissed after the user approves a shot
          (or hits the × on the card). Reduces bad submissions caused by
          overhead-light glare and shadow under the card edge. */}
      {liveActive && !tipsDismissed && step === 0 && (
        <div style={{
          background: '#fff8e1', border: '1px solid #f59e0b', borderRadius: 8,
          padding: 12, marginBottom: 12, position: 'relative', fontSize: 13,
        }}>
          <strong style={{ display: 'block', marginBottom: 6 }}>📸 Capture tips</strong>
          <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
            <li>Use indirect, diffuse light — a window or lamp bouncing off a wall.</li>
            <li>Tilt the card 5–10° away from the brightest source to avoid glare.</li>
            <li>Hold the phone parallel; rest your elbows for steadiness.</li>
            <li>Plain dark background (a phone case, mousepad) helps the auto-crop.</li>
          </ul>
          <button
            type="button"
            aria-label="Dismiss tips"
            onClick={() => setTipsDismissed(true)}
            style={{
              position: 'absolute', top: 6, right: 6,
              background: 'transparent', border: 'none',
              fontSize: 18, lineHeight: 1, cursor: 'pointer', padding: 4,
            }}
          >×</button>
        </div>
      )}

      {/* Always-mounted viewfinder wrapper. Visibility is controlled by
          `display` so the <video> child stays in the DOM across stage
          transitions, which iOS requires. */}
      <div
        style={{
          position: 'relative',
          background: '#000',
          borderRadius: 12,
          overflow: 'hidden',
          width: '100%',
          maxWidth: 480,
          margin: '0 auto',
          aspectRatio: '5 / 7',
          display: liveActive ? 'block' : 'none',
        }}
        onClick={() => videoRef.current?.play?.().catch(() => {})}
      >
        <video
          ref={videoRef}
          playsInline
          muted
          autoPlay
          webkit-playsinline="true"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            cursor: 'pointer',
          }}
        />
        {liveActive && <>
          <Overlay sharp={sharpness >= SHARP_OK} sharpness={sharpness} glare={glare} />
          <div style={{ position: 'absolute', top: 8, left: 8, right: 130, color: '#fff', textShadow: '0 1px 2px #000', fontSize: 14, zIndex: 2 }}>
            <strong>Step {step + 1} / {plan.length}</strong>
            <div style={{ fontSize: 12, opacity: 0.92 }}>{currentTask.label}</div>
          </div>
          {/* Top-right controls stack: torch (when supported) + auto-capture toggle */}
          <div style={{
            position: 'absolute', top: 8, right: 8, zIndex: 4,
            display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end',
          }}>
            {torchSupported && (
              <button
                type="button"
                onClick={toggleTorch}
                style={{
                  background: torchOn ? '#fbbf24' : 'rgba(0,0,0,0.55)',
                  color: torchOn ? '#000' : '#fff',
                  border: 'none', borderRadius: 999, padding: '4px 10px',
                  fontSize: 12, fontWeight: 600, cursor: 'pointer',
                }}
                title="Toggle flashlight"
                aria-pressed={torchOn}
              >
                {torchOn ? '💡 Light ON' : '💡 Light OFF'}
              </button>
            )}
            <label style={{
              display: 'flex', alignItems: 'center', gap: 6, fontSize: 12,
              background: 'rgba(0,0,0,0.55)', color: '#fff',
              padding: '4px 8px', borderRadius: 999, cursor: 'pointer',
              userSelect: 'none',
            }}>
              <input
                type="checkbox" checked={autoCapture}
                onChange={e => setAutoCapture(e.target.checked)}
                style={{ accentColor: '#22c55e' }}
              />
              Auto-capture
            </label>
          </div>

          {pending ? (
            // ── Confirmation preview: still image + Use this / Retake ──
            <>
              <img
                src={pending.url}
                alt="captured preview"
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 3 }}
              />
              <div style={{
                position: 'absolute', top: 36, left: 8, right: 8, zIndex: 4,
                background: 'rgba(0,0,0,0.6)', color: '#fff', padding: '6px 10px',
                borderRadius: 6, fontSize: 13, textAlign: 'center',
              }}>
                Use this shot for <strong>{pending.field}</strong>?
              </div>
              <div style={{ position: 'absolute', bottom: 8, left: 8, right: 8, display: 'flex', gap: 8, zIndex: 4 }}>
                <button type="button" onClick={approvePending} style={{ ...btnPrimary, flex: 1, background: '#16a34a' }}>
                  ✓ Use this
                </button>
                <button type="button" onClick={rejectPending} style={{ ...btnGhost, flex: 1 }}>
                  ↻ Retake
                </button>
              </div>
            </>
          ) : (
            <div style={{ position: 'absolute', bottom: 8, left: 8, right: 8, display: 'flex', gap: 8, zIndex: 2 }}>
              <button type="button" onClick={() => { captureLockRef.current = true; doCapture(); }} style={btnPrimary}>
                📸 Capture
              </button>
              <button type="button" onClick={() => { setUseFiles(true); stopCamera(); setStage('idle'); }} style={btnGhost}>
                Use upload
              </button>
            </div>
          )}
        </>}
      </div>

      {!useFiles && stage === 'live' && allDone && (
        <div style={{ padding: 12, background: '#e8f5e9', borderRadius: 8, marginBottom: 12 }}>
          All {plan.length} captures done. Review thumbnails below, retake any you don't like, then Submit.
        </div>
      )}

      {/* Captures grid with per-shot validation status */}
      {Object.keys(captured).length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10, marginTop: 12 }}>
          {plan.map((t, i) => {
            const blob = captured[t.field];
            const v = validations[t.field];
            return (
              <div key={t.field} style={{ border: '1px solid #ddd', borderRadius: 6, padding: 6, fontSize: 12 }}>
                {blob ? (
                  <div style={{ position: 'relative' }}>
                    <img src={URL.createObjectURL(blob)} alt={t.field}
                         style={{ width: '100%', aspectRatio: '5/7', objectFit: 'cover', borderRadius: 4 }} />
                    {/* Status badge top-left */}
                    <ValidationBadge v={v} />
                    {/* Delete badge top-right */}
                    <button
                      type="button"
                      aria-label={`Delete ${t.field}`}
                      onClick={() => retake(i)}
                      title="Delete this capture"
                      style={{
                        position: 'absolute', top: 4, right: 4,
                        width: 28, height: 28, borderRadius: '50%',
                        background: 'rgba(220,38,38,0.95)', color: '#fff',
                        border: 'none', cursor: 'pointer', fontSize: 16,
                        lineHeight: '24px', padding: 0, fontWeight: 700,
                      }}
                    >×</button>
                    <div style={{ marginTop: 6, fontWeight: 600 }}>{t.field}</div>
                    {v?.status === 'fail' && v.issues?.[0] && (
                      <div style={{ fontSize: 11, color: '#b91c1c', marginTop: 4 }}>
                        {v.issues[0].message}
                      </div>
                    )}
                    {v?.status === 'ok' && v.issues?.some(i => i.severity === 'warning') && (
                      <div style={{ fontSize: 11, color: '#b45309', marginTop: 4 }}>
                        {v.issues.find(i => i.severity === 'warning').message}
                      </div>
                    )}
                    <button type="button" onClick={() => retake(i)}
                            style={{ ...btnGhost, padding: '6px 10px', minHeight: 36, fontSize: 12, marginTop: 6, width: '100%' }}>
                      Retake
                    </button>
                  </div>
                ) : (
                  <div style={{ aspectRatio: '5/7', display: 'grid', placeItems: 'center', color: '#888' }}>
                    {t.field}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pre-submit summary: which captures are ready, which need retake */}
      {Object.keys(captured).length > 0 && (
        <PreSubmitSummary plan={plan} validations={validations} captured={captured} onRetake={retake} />
      )}

      {/* File-upload fallback panel */}
      {useFiles && (
        <div style={{ marginTop: 12, padding: 12, border: '1px dashed #aaa', borderRadius: 8 }}>
          <p>Upload your card photos. The front is required; back is optional.</p>
          <label style={{ display: 'block', marginBottom: 8 }}>
            FRONT <input type="file" accept="image/jpeg,image/png,image/webp" onChange={onFiles('front')} />
          </label>
          {wantBack && (
            <label style={{ display: 'block' }}>
              BACK <input type="file" accept="image/jpeg,image/png,image/webp" onChange={onFiles('back')} />
            </label>
          )}
        </div>
      )}

      <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" onClick={submit} disabled={submitDisabled}
                style={{ ...btnPrimary, opacity: submitDisabled ? 0.5 : 1, cursor: submitDisabled ? 'not-allowed' : 'pointer' }}
                title={anyFailed ? 'Retake the failed shots first' : ''}>
          {busy ? 'Grading…' : anyFailed ? 'Fix failed shots first' : 'Submit for grading'}
        </button>
        {Object.keys(captured).length > 0 && (
          <button type="button" onClick={startOver} style={{ ...btnGhost, color: '#b91c1c', borderColor: '#fca5a5' }}>
            Start over
          </button>
        )}
        {!useFiles && (
          <button type="button" onClick={() => setUseFiles(true)} style={btnGhost}>
            Switch to file upload
          </button>
        )}
        {useFiles && (
          <button type="button" onClick={() => { setUseFiles(false); setStage('idle'); }} style={btnGhost}>
            Switch to camera
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Status badge overlaid on a capture thumbnail. Three states:
 *   pending  ⋯  spinner / "checking…"
 *   ok       ✓  green
 *   fail     ✗  red
 */
function ValidationBadge({ v }) {
  let bg = '#94a3b8', label = '⋯', title = 'Checking…';
  if (v?.status === 'ok') {
    bg = '#16a34a';
    label = '✓';
    const warn = v.issues?.find(i => i.severity === 'warning');
    title = warn ? warn.message : 'Looks good';
  } else if (v?.status === 'fail') {
    bg = '#dc2626';
    label = '✗';
    title = v.issues?.[0]?.message || 'Retake needed';
  }
  return (
    <div title={title}
         style={{
           position: 'absolute', top: 4, left: 4,
           width: 26, height: 26, borderRadius: '50%',
           background: bg, color: '#fff', fontWeight: 700,
           display: 'grid', placeItems: 'center', fontSize: 14,
           boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
         }}>
      {label}
    </div>
  );
}

/**
 * Pre-submit summary panel — shows EVERY required slot with its current
 * state (missing / checking / good / failed) and surfaces failure reasons
 * with a Retake button. Submit button at the parent disables until all
 * required slots are at least 'ok' or 'pending' (no failures).
 */
function PreSubmitSummary({ plan, validations, captured, onRetake }) {
  const rows = plan.map((t, i) => {
    const has = !!captured[t.field];
    const v = validations[t.field];
    let state = !has ? 'missing'
              : v?.status === 'ok'      ? 'ok'
              : v?.status === 'fail'    ? 'fail'
              : v?.status === 'pending' ? 'pending'
              :                            'unknown';
    const reason = v?.issues?.[0]?.message || (state === 'missing' ? 'Not captured yet' : '');
    return { idx: i, field: t.field, state, reason };
  });
  const failCount   = rows.filter(r => r.state === 'fail').length;
  const okCount     = rows.filter(r => r.state === 'ok').length;
  const total       = rows.length;
  const allOkOrPending = rows.every(r => r.state === 'ok' || r.state === 'pending');

  const headerColor =
    failCount > 0      ? '#b91c1c' :
    allOkOrPending && okCount === total ? '#15803d' :
                                          '#475569';
  const headerBg =
    failCount > 0      ? '#fee2e2' :
    allOkOrPending && okCount === total ? '#dcfce7' :
                                          '#f1f5f9';

  return (
    <div style={{ marginTop: 16, padding: 12, background: headerBg, border: `1px solid ${headerColor}33`, borderRadius: 8 }}>
      <div style={{ fontWeight: 700, color: headerColor, marginBottom: 8 }}>
        {failCount > 0
          ? `${failCount} shot${failCount > 1 ? 's' : ''} need${failCount > 1 ? '' : 's'} to be retaken`
          : okCount === total
            ? 'All shots ready to submit ✓'
            : `Pre-flight check (${okCount} / ${total} verified)`}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 6, fontSize: 13 }}>
        {rows.map(r => (
          <div key={r.field} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', padding: '6px 8px', borderRadius: 4, border: '1px solid #e5e7eb' }}>
            <SummaryDot state={r.state} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600 }}>{r.field}</div>
              {r.reason && <div style={{ fontSize: 11, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.reason}</div>}
            </div>
            {r.state === 'fail' && (
              <button type="button" onClick={() => onRetake(r.idx)}
                      style={{ padding: '4px 10px', fontSize: 12, border: '1px solid #fca5a5', background: '#fff', color: '#b91c1c', borderRadius: 4, cursor: 'pointer' }}>
                Retake
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function SummaryDot({ state }) {
  const map = {
    ok:      { bg: '#16a34a', label: '✓' },
    fail:    { bg: '#dc2626', label: '✗' },
    pending: { bg: '#94a3b8', label: '⋯' },
    missing: { bg: '#cbd5e1', label: '○' },
    unknown: { bg: '#cbd5e1', label: '?' },
  };
  const { bg, label } = map[state] || map.unknown;
  return (
    <div style={{
      width: 22, height: 22, borderRadius: '50%',
      background: bg, color: '#fff', fontWeight: 700,
      display: 'grid', placeItems: 'center', fontSize: 12, flexShrink: 0,
    }}>{label}</div>
  );
}

function Overlay({ sharp, sharpness, glare }) {
  // Card-aspect SVG overlay drawn over the video. Glare warning takes
  // precedence over the regular sharp/blur colours since it's an actionable
  // capture-quality issue, not just "wait a moment".
  const stroke = glare ? '#dc2626' : (sharp ? '#22c55e' : '#f59e0b');
  return (
    <>
      <svg viewBox="0 0 100 140" preserveAspectRatio="xMidYMid meet"
           style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
        <rect x="0.5" y="0.5" width="99" height="139" rx="2" ry="2" fill="none" stroke={stroke} strokeWidth="0.7" />
        {[
          [0.5, 0.5], [99.5, 0.5], [0.5, 139.5], [99.5, 139.5],
        ].map(([x, y], i) => (
          <g key={i} stroke={stroke} strokeWidth="1">
            <line x1={x - (i % 2 ? 4 : -4)} y1={y} x2={x} y2={y} />
            <line x1={x} y1={y - (i < 2 ? -4 : 4)} x2={x} y2={y} />
          </g>
        ))}
        <text x="50" y="135" textAnchor="middle" fontSize="3" fill="#fff" stroke="#000" strokeWidth="0.1">
          sharpness {Math.round(sharpness)}
        </text>
      </svg>
      {glare && (
        <div style={{
          position: 'absolute', bottom: 56, left: '50%', transform: 'translateX(-50%)',
          background: 'rgba(220,38,38,0.92)', color: '#fff',
          padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600,
          zIndex: 3, pointerEvents: 'none', whiteSpace: 'nowrap',
        }}>
          ⚠ Glare detected — tilt the card slightly to diffuse the light
        </div>
      )}
    </>
  );
}

const btnPrimary = {
  padding: '12px 18px', minHeight: 44, borderRadius: 6, border: 'none',
  background: '#2563eb', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 14,
};
const btnGhost = {
  padding: '12px 18px', minHeight: 44, borderRadius: 6, border: '1px solid #ccc',
  background: '#fff', color: '#333', cursor: 'pointer', fontSize: 14,
};
